import { Box } from '@material-ui/core';
import {
  AllDocumentationRequired,
  AssetType,
  BorrowerFileType,
  BorrowerOnboardingDetails,
  getAllDocumentationRequiredByLoanId,
  getLoanById,
  Loan,
  OnboardingDetailsRole,
} from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { DocumentsValidationProcess } from '@/components/modules/DocumentsValidation/components/DocumentsValidationProcess';
import { VALIDATION_EXCLUDED_FILE_TYPES } from '@/components/modules/DocumentsValidation/constants';
import { DocumentsValidationContextProvider } from '@/components/modules/DocumentsValidation/contexts/DocumentsValidationContext';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { PendingDataType } from '@/config/types';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCache } from '@/hooks/requests/useCache';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

export const DocumentsValidation = () => {
  const { loanId } = useParams();

  if (!loanId) {
    return null;
  }

  return <DocumentsValidationByLoanId loanId={loanId} />;
};

export const DocumentsValidationByLoanId = ({ loanId }: { loanId: string }) => {
  const { actions: pageActions } = usePageContext();
  const { data: loan, loading, revalidate: revalidateLoan } = useCache(getLoanById, { loanId });
  const { data: allDocumentationRequired, revalidate: revalidateDocumentation } = useCache(
    getAllDocumentationRequiredByLoanId,
    { loanId },
  );

  const revalidateValues = async () => {
    const loan: Loan = await revalidateLoan();
    const documentation: AllDocumentationRequired = await revalidateDocumentation();
    return { loan, documentation: filterBorrowerDocumentationRequiredByExistingFiles(documentation) };
  };

  useEffect(() => {
    if (!loading && !loan) {
      pageActions.setNotFound(true);
    }
  }, [loading, loan]);

  if (!loan || loan.assetReference[0].type !== AssetType.CARD) {
    return (
      <Box maxWidth={'800px'}>
        <PendingDataWarning type={PendingDataType.NO_LOAN_FOUND} />
      </Box>
    );
  }

  if (!allDocumentationRequired) {
    return (
      <Box maxWidth={'800px'}>
        <PendingDataWarning type={PendingDataType.NO_DOCUMENTATION_FOUND} />
      </Box>
    );
  }

  const borrowerOnboardingDetails = loan.onboardingDetails.find(
    (onboarding) => onboarding.role === OnboardingDetailsRole.BORROWER,
  ) as BorrowerOnboardingDetails;

  if (!borrowerOnboardingDetails) {
    throw new Error(`Can't find borrower onboardingDetails in loan: ${loan.id}`);
  }

  const filterBorrowerDocumentationRequiredByExistingFiles = (allDocumentationRequired: AllDocumentationRequired) => {
    const borrower = allDocumentationRequired.borrowers[0];
    const requiredFiles =
      borrower.requiredFiles?.filter(
        (file) => file.uploadedFiles?.length && !VALIDATION_EXCLUDED_FILE_TYPES.includes(file.name as BorrowerFileType),
      ) ?? [];
    return { ...borrower, requiredFiles };
  };

  return (
    <DocumentsValidationContextProvider
      data={{
        loan,
        borrowerDocumentationRequired: filterBorrowerDocumentationRequiredByExistingFiles(allDocumentationRequired),
        borrowerOnboardingDetails,
      }}
    >
      <DocumentsValidationProcess revalidateValues={revalidateValues} />
    </DocumentsValidationContextProvider>
  );
};

export const DocumentsValidationModule = withPermissionsModule(DocumentsValidation);

// Testing

// http://localhost:3001/loan/77d29f0b-d8f1-4367-a071-683ac2d601a0/validation/
// 47866056C
// (dev)

// http://localhost:3001/loan/8687af4a-bb4b-49af-94b7-7581989689ae/validation/
// 75590769G
// (dev)

// http://localhost:3001/loan/61aff460-e1ab-4200-849b-e42ace2eeab0/validation/
// 47865965K
// (stg)
